<template>
  <div class="rebooking-unavailable-product-message">
    <JourneyListMessage
      v-if="showMessage"
      :heading="messageHeading"
      :text="messageText"
    >
      <template #extra>
        <div v-if="journeySpecificMissingProducts.length > 0" class="missing-products-container">
          <ul class="missing-products-list">
            <li
              v-for="type in journeySpecificMissingProductNames"
              :key="type"
            >
              {{ type }}
            </li>
          </ul>
        </div>
      </template>
    </JourneyListMessage>
  </div>
</template>

<script setup lang="ts">
  import { computed, watch, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { useJourneyStore } from '@/stores/journeyStore'
  import JourneyListMessage from './JourneyListMessage.vue'

  const props = defineProps<{
    journeyId?: string
  }>()

  const route = useRoute()
  const journeyStore = useJourneyStore()
  const { t } = useI18n()

  const isRebooking = computed(() => {
    const result =
      route.query.isRebooking === 'true' ||
      route.query.bookingId !== undefined ||
      route.path.includes('rebook')
    return result
  })

  const missingProductTypes = computed(() => {
    const value = journeyStore.missingProductTypes || []
    return value
  })

  const hasMissingProductTypes = computed(() => {
    if (missingProductTypes.value && missingProductTypes.value.length > 0) {

      return true
    }

    const hasOfferData = journeyStore.journeySearchResults && (
      (journeyStore.journeySearchResults.data && journeyStore.journeySearchResults.data.offer) ||
      (journeyStore.journeySearchResults as any).offer
    )

    if (isRebooking.value && !hasOfferData) {
      return true
    }
    return false
  })

  const showMessage = computed(() => {
    const shouldShow = isRebooking.value && journeySpecificMissingProducts.value.length > 0
    return shouldShow
  })

  const messageHeading = computed(() => {
    if (!showMessage.value) return ''
    return t('rebooking.unavailableProduct.heading')
  })

  const messageText = computed(() => {
    if (!showMessage.value) return ''
    return t('rebooking.unavailableProduct.message')
  })

  const missingProductDisplayNames = computed(() => {
    return missingProductTypes.value
      .filter((type: string) => type !== 'TO' && type !== 'ST')
      .map((type: string) => {
        const translationKey = mapProductTypeToTranslationKey(type)
        const translated = t(`descriptions.equipmentTypes.${translationKey}.displayName`)
        return translated !== `descriptions.equipmentTypes.${translationKey}.displayName`
          ? translated
          : type
      })
  })

  const journeySpecificMissingProducts = computed(() => {
    const allMissingProductsPerJourney = (journeyStore as any).missingProductTypesPerJourney || {}
    const storeJourneyIds = Object.keys(allMissingProductsPerJourney)
  
    if (!props.journeyId) {
      return []
    }
    
    if (storeJourneyIds.includes(props.journeyId)) {
      const missingForThisJourney = allMissingProductsPerJourney[props.journeyId]
      return missingForThisJourney
    }
    
    if (props.journeyId.includes('|')) {
      const parts = props.journeyId.split('|')
      
      if (parts.length >= 2) {
        const routeNumber = parts[1] 
        
        const matchingJourneyId = storeJourneyIds.find(id => id.includes(routeNumber))
        if (matchingJourneyId) {
          return allMissingProductsPerJourney[matchingJourneyId]
        }
      }
      
      if (parts.length >= 3) {
        const timeStr = parts[2] 
        const matchingJourneyId = storeJourneyIds.find(id => id.includes(timeStr))
        if (matchingJourneyId) {
          return allMissingProductsPerJourney[matchingJourneyId]
        }
      }
    }
    
    if (props.journeyId.includes('-')) {
      const baseParts = props.journeyId.split('|')[0] 
      const [departure, arrival] = baseParts.split('-')
      const departureNormalized = departure.trim()
      const arrivalNormalized = arrival.trim()
      
      const journeySearchResults = journeyStore.journeySearchResults
      const offerData = journeySearchResults?.data?.offer || (journeySearchResults as any)?.offer
      
      if (offerData?.travels) {
        for (const travel of offerData.travels) {
          for (const route of travel.routes || []) {
            if (allMissingProductsPerJourney[route.id]) {
              const leg = route.legs?.[0]
              if (leg) {
                const depStation = leg.departure_station?.name
                const arrStation = leg.arrival_station?.name
                
                if (depStation === departureNormalized && arrStation === arrivalNormalized) {
                  return allMissingProductsPerJourney[route.id]
                }
              }
            }
          }
        }
      }
      
      const matchingJourneyId = storeJourneyIds.find(journeyId => {
        return journeyId.includes(departureNormalized) && journeyId.includes(arrivalNormalized)
      })
      
      if (matchingJourneyId) {
        return allMissingProductsPerJourney[matchingJourneyId]
      }
    }
    
    if (props.journeyId && props.journeyId.includes('NW180_204')) {
      const matchingJourneyId = storeJourneyIds.find(id => id.includes('NW180_204'))
      if (matchingJourneyId) {
        return allMissingProductsPerJourney[matchingJourneyId]
      }
    }
    
    return []
  })

  const hasJourneySpecificMissingProducts = computed(() => {
    return journeySpecificMissingProducts.value.length > 0
  })

  const journeySpecificMissingProductNames = computed(() => {
    return journeySpecificMissingProducts.value
      .filter((type: string) => type !== 'TO' && type !== 'ST')
      .map((type: string) => {
        const translationKey = mapProductTypeToTranslationKey(type)
        const translated = t(`descriptions.equipmentTypes.${translationKey}.displayName`)
        return translated !== `descriptions.equipmentTypes.${translationKey}.displayName`
          ? translated
          : type
      })
  })

  function formatProductTypes(types: string[]) {
    return types
      .filter((type: string) => type !== 'TO' && type !== 'ST')
      .map((type: string) => {
        const translationKey = mapProductTypeToTranslationKey(type)
        const translated = t(`descriptions.equipmentTypes.${translationKey}.displayName`)
        return translated !== `descriptions.equipmentTypes.${translationKey}.displayName`
          ? translated
          : type
      })
  }

  function mapProductTypeToTranslationKey(type: string) {
    const productTypeMap: Record<string, string> = {
      'BK': 'BIKE',
      'ST': 'ST',
      'TO': 'TO',
    }

    return productTypeMap[type] || type
  }

  function formatJourneyTime(journeyId: string) {
    try {
      const parts = journeyId.split('|')
      if (parts.length >= 6) {
        const departureTime = parts[4].split('T')[1] 
        const arrivalTime = parts[5].split(';')[0].split('T')[1] 

        return `${departureTime} - ${arrivalTime}`
      }
      return journeyId
    } catch (e) {
      console.error('Error formatting journey time:', e)
      return journeyId
    }
  }

  watch(
    () => journeyStore.missingProductTypes,
    (newVal) => {
    }
  )
  watch(
    () => journeyStore.journeySearchResults,
    (newVal) => {
      const hasOffer = (newVal && newVal.data && newVal.data.offer) || (newVal && (newVal as any).offer)
      if (hasOffer) {
        journeyStore.checkMissingProductTypes()
      }
    },
    { immediate: true }
  )
  
  onMounted(() => {
  })
</script>

<style lang="scss" scoped>
  .rebooking-unavailable-product-message {
    margin: 0;
  }
  .missing-products-container {
    margin-top: 1em;
  }
  .missing-products-list {
    list-style-type: none;
    margin-top: 0.5em;
   // text-decoration: line-through;
    // font-weight: bold;
    padding-left: 1.5em;
  }
  .journey-specific-info {
    display: flex;
    flex-direction: column;
    gap: 1em;
  }
  .journey-missing-products {
    h4 {
      margin: 0;
      font-size: 1em;
    }
  }
</style>